import I18n from 'i18n-js';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { animated as a, useSpring } from 'react-spring';
import swal from 'sweetalert';
import WebFont from 'webfontloader';
import { useT } from '../../i18n/LanguageProvider';
import LanguageSwitcher from '../../i18n/LanguageSwitcher';
import CardViewer from '../../components/CardViewer';
import SingleCardViewer, { prefersReducedMotion } from '../../components/CardViewer/SingleCardViewer';
import PostcardViewer from '../../components/CardViewer/PostcardViewer';
import CardViewerMobile from '../../components/CardViewerMobile';
import EnvelopeIntro from '../../components/EnvelopeIntro';
import Modal from '../../components/Modal/index';
import ToolTipCard from '../../components/ToolTipPanel/ToolTipCard';
import { DesignToolContext } from '../../contexts/DesignTool/DesignToolContext';
import { TMergeTag } from '../../contexts/DesignTool/types';
import { analyticsService } from '../../services/request/analytics';
import { card_services } from '../../services/request/card';
import { custom_domain_services } from '../../services/request/customDomain';
import { workspace_custom_domain_services } from '../../services/request/workspaceCustomDomain';
import { recipients_service } from '../../services/request/recipients';
import CardPage from './CardPage';
import { ImageObj } from './types';
// import { useSelector } from 'react-redux';
import Confetti from 'react-dom-confetti';
import { workspace_services } from '../../services/request/workspace';
import Konva from 'konva';

import { ViewCardContext } from './ViewCardContext';
import ReactionBar from './engagement/ReactionBar';
import CommentSection from './engagement/CommentSection';
import DownloadButton from './engagement/DownloadButton';
import EventPanel from './engagement/EventPanel';
import ShareButton from './engagement/ShareButton';
import MusicPlayer from './engagement/MusicPlayer';
import { videoRenderService, VideoRenderStatus } from '../../services/request/videoRender';
import TileBoardViewer from './TileBoardViewer';
import BoardEffects from '../../components/BoardEffects/BoardEffects';
import { markImageMergeTags, resolveImageMergeTags } from '../../utils/imageMergeTags';
import { coverImageUrl as dynamicCoverImageUrl, isDynamicCover } from '../../utils/dynamicCover';
import { pageHasContent, scalePage } from '../../utils/lastPage';
import { assembleForFormat, formatOf } from '../../utils/cardFormat';

if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
	Konva.pixelRatio = 1;
}

interface Props {
	isPreviewModal?: any;
	props?: any;
	cardId?: any;
	selectedTemplate?: any;
}

const ViewCard = ({ isPreviewModal, cardId, selectedTemplate }: Props) => {
	// set our merge tags format to _.template
	_.templateSettings.interpolate = /{{([\s\S]+?)}}/g;
	const { t } = useT();
	const [newPages, setNewPages] = useState<{ (): JSX.Element }[]>([]);
	const [openCard, setopenCard] = useState(true);
	const [cardFormat, setCardFormat] = useState(formatOf());
	const [open, setOpen] = useState(true);
	const [currentWorkspace, setCurrentWorkspace] = useState<any>();
	const [isCTA, setCTA] = useState(false);
	const [ctaLink, setCTALink] = useState<string>('https://www.example.com');
	const [cardHeader, setCardHeader] = useState<string>('');
	const [ctaText, setCTAText] = useState<string>('Click here');
	const [recipientName, setRecipientName] = useState<string>('');
	const [senderName, setSenderName] = useState<string>('');
	const [isConfettiOn, setConfetti] = useState(false);
	const [coverImageUrl, setCoverImageUrl] = useState<string>('');
	const [cardTitle, setCardTitle] = useState<string>('');
	const [hasScrolled, setHasScrolled] = useState(false);
	// "Coming out of the envelope" intro — shown once to recipients before the card.
	const [showEnvelope, setShowEnvelope] = useState(true);
	const [fullCardData, setFullCardData] = useState<any>(null);
	const [renderedVideoUrl, setRenderedVideoUrl] = useState<string | null>(null);
	const scrollWrapperRef = useRef<HTMLDivElement>(null);
	const { previewCardOptions, mergeTags, selectedCardId } = useContext(DesignToolContext);

	const [width, setWidth] = React.useState(window.innerWidth);
	const breakpoint = 620;
	const desktopBreakpoint = 1280;

	useEffect(() => {
		/* Inside of a "useEffect" hook add an event listener that updates
	   the "width" state variable when the window size changes */
		window.addEventListener('resize', () => setWidth(window.innerWidth));

		/* passing an empty array as the dependencies of the effect will cause this
	   effect to only run when the component mounts, and not each time it updates.
	   We only want the listener to be added once */
	}, []);

	// Show/hide the scroll-down hint based on scroll position
	useEffect(() => {
		const el = scrollWrapperRef.current;
		if (!el) return;
		const onScroll = () => {
			setHasScrolled(el.scrollTop > 50);
		};
		el.addEventListener('scroll', onScroll, { passive: true });
		return () => el.removeEventListener('scroll', onScroll);
	}, []);

	let { id }: { id?: any } = useParams();
	let [searchParams] = useSearchParams();
	const recipientId: any = searchParams.get('recipientId');
	// per-recipient link token (`t`), required by the RSVP endpoints
	const rsvpToken: string = searchParams.get('t') || '';
	const recipientEmailFromParams: string = searchParams.get('email') || '';
	const isPreviewParam = searchParams.get('preview') === 'true';
	const isPreview = !!isPreviewModal || isPreviewParam;
	if (!id) {
		id = cardId || selectedCardId;
	}
	const routePath = window.location.pathname;
	const handleGetWorkspace = async (workspaceId: string) => {
		try {
			const res = await workspace_services.getWorkspaceByID(workspaceId);
			setCurrentWorkspace(res);
		} catch (error) {
			console.log('  error:', error);
		}
	};
	const getCardData = useCallback(async () => {
		if (id) {
			let somePages: { (): JSX.Element }[] = [];
			// NOTE - need logged in user to call firebase
			// if (!firebaseAuthServices.getCurrentUser() && !!id) await firebaseAuthServices.collaboratorSignIn();
			let res = await card_services.getCardById(id);
			setFullCardData(res);
			// Check for rendered video if this is a video card
			if (res?.isVideoCard && routePath.includes('viewcard')) {
				try {
					const renderStatus = await videoRenderService.getRenderStatus(id);
					if (renderStatus?.status === 'COMPLETED' && renderStatus.outputUrl) {
						setRenderedVideoUrl(renderStatus.outputUrl);
					}
				} catch (e) {
					// No render available — show normal card
				}
			}
			if (routePath.includes('viewcard')) {
				if (res && res.emailPreviewData) {
					if (res.emailPreviewData.isCTA) {
						setCTA(res.emailPreviewData.isCTA);
					}
					if (res.emailPreviewData.ctaLink) {
						setCTALink(res.emailPreviewData.ctaLink);
					}
					if (res.emailPreviewData.ctaText) {
						setCTAText(res.emailPreviewData.ctaText);
					}
					if (res.emailPreviewData.cardPreviewHeader) {
						setCardHeader(res.emailPreviewData.cardPreviewHeader);
					}
					if (res.emailPreviewData.isConfettiOn) {
						setConfetti(res.emailPreviewData.isConfettiOn);
					}
				}

				if (res.deliveryInfo && res.deliveryInfo.fromName) {
					setSenderName(res.deliveryInfo.fromName);
				}
				if (res.workspace_id) {
					handleGetWorkspace(res.workspace_id);
				}
			}
			try {
				const { id: cardId, ...cardData } = res;
				if (cardData.title) setCardTitle(cardData.title);
				if (cardData.stage) {
					const compileTemplate = _.template(JSON.stringify(cardData.cardPages));
					let compiledCardPages = cardData.cardPages;
					// raw tag values (no text fallbacks) used to personalize images bound to merge tags
					let imageTagValues: Record<string, unknown> = {};
					// dynamic covers: the values the backend renders the envelope's cover image with
					let coverRenderParams: Record<string, string> = {};
					// if previewing card for prospects using merge tags then replace merge tags in cardpages
					if (isPreviewModal && previewCardOptions.previewProspect) {
						imageTagValues = previewCardOptions.prospectData || {};
						coverRenderParams = { ...(previewCardOptions.prospectData || {}) };
						let dataWithFallback = previewCardOptions.prospectData;
						Object.keys(previewCardOptions.prospectData).forEach((key: string) => {
							if (!previewCardOptions.prospectData[key]) {
								const fallback = mergeTags.find((item) => item.tagName === key)?.fallback || '';
								dataWithFallback = {
									...dataWithFallback,
									[key]: fallback,
								};
							}
						});
						try {
							compiledCardPages = JSON.parse(compileTemplate(dataWithFallback));
						} catch (error) {
							console.log({ error });
							swal({
								icon: 'warning',
								title: 'Incorrect merge tag(s)!',
								text: 'One or more merge tags were missing or incorrect.',
							});
						}
					} else if (recipientId) {
						const recipientData = await recipients_service.getRecipientById(recipientId);
						coverRenderParams = { recipientId };
						setRecipientName(recipientData.mergeTags[0].value);
						let recipientTagsData: any = {};
						if (recipientData) {
							recipientData.mergeTags.forEach((item: TMergeTag) => {
								recipientTagsData = {
									...recipientTagsData,
									[item.tagName]: item.value || item.fallback || '',
								};
								imageTagValues[item.tagName] = item.value;
							});
						}
						try {
							compiledCardPages = JSON.parse(compileTemplate(recipientTagsData));
						} catch (error) {
							console.log({ error });
						}
					} else if (!recipientId && (() => {
						const p: any = {};
						searchParams.forEach((v, k) => { if (k !== 'recipientId') p[k] = v; });
						return Object.keys(p).length > 0;
					})()) {
						// URL query param merge tags (for external email platforms)
						const urlParamData: any = {};
						searchParams.forEach((value, key) => {
							if (key !== 'recipientId') urlParamData[key] = value;
						});
						imageTagValues = { ...urlParamData };
						coverRenderParams = { ...urlParamData };
						// Fetch card merge tags to get fallback values for any tags not in URL
						try {
							const mergeTagRes = await card_services.getCardByIdForMergeTag(id);
							if (mergeTagRes?.mergeTags?.length) {
								mergeTagRes.mergeTags.forEach((tag: TMergeTag) => {
									if (urlParamData[tag.tagName] === undefined || urlParamData[tag.tagName] === '') {
										urlParamData[tag.tagName] = tag.fallback || '';
									}
								});
							}
						} catch (err) {
							console.log('Error fetching merge tags for URL params:', err);
						}
						const firstValue = Object.values(urlParamData).find(Boolean) as string;
						if (firstValue) setRecipientName(firstValue);
						try {
							compiledCardPages = JSON.parse(compileTemplate(urlParamData));
						} catch (error) {
							console.log({ error });
						}
					} else {
						let noProspectPreview: any = {};
						mergeTags.forEach((item) => {
							noProspectPreview[item.tagName] = item.fallback;
						});
						try {
							compiledCardPages = JSON.parse(compileTemplate(noProspectPreview));
						} catch (error) {
							console.log({ error });
							// swal({
							// 	icon: 'warning',
							// 	title: 'Incorrect merge tag(s)!',
							// 	text: 'One or more merge tags were missing or incorrect.',
							// });
						}
					}
					const dynamicCover = isDynamicCover(cardData);
					compiledCardPages = resolveImageMergeTags(compiledCardPages, imageTagValues, dynamicCover);
					// sender preview without prospect data: mark which images are replaced per recipient
					if (isPreviewModal && !previewCardOptions.previewProspect) compiledCardPages = markImageMergeTags(compiledCardPages, dynamicCover);
					const updatedCardPages = await Promise.all(
						compiledCardPages.map(async (pageData: any) => {
							return pageData?.images?.length
								? await Promise.all(
									pageData?.images?.map(async (image: ImageObj, imageIndex: number) => {
										if (image.imageUrl || image.downloadUrl) {
											if (image.imageUrl) pageData.images[imageIndex].imageUrl = image.imageUrl;
											if (image.downloadUrl) pageData.images[imageIndex].downloadUrl = image.downloadUrl;
											return pageData;
										}
									})
								)
								: [pageData];
							// NOTE - return [pageData] because out expected output is an array of object(object being updated page data with blobs)
						})
					);
					// workspace default last page (static, never merge-tagged) is scaled to this card and always shown last
					const lastPage = pageHasContent(cardData.defaultLastPage?.page)
						? scalePage(cardData.defaultLastPage.page, cardData.defaultLastPage.stage, cardData.stage)
						: undefined;
					const checkedCardPages = assembleForFormat(updatedCardPages, lastPage, cardData.format);
					checkedCardPages.forEach((pageData: any, pageId: any) => {
						// NOTE - because of array.map each page is an array containing cardPage object at pageData[0]

						somePages.push(() => <CardPage stage={cardData.stage} pageData={pageData[0]} pageId={pageId} />);
					});
					// Store cover image URL for direct download
					const firstPage = checkedCardPages[0]?.[0];
					const firstImage = firstPage?.images?.[0];
					if (dynamicCover) {
						// the envelope shows the cover as one image: the backend's render for this recipient
						setCoverImageUrl(dynamicCoverImageUrl(id as string, coverRenderParams));
					} else if (firstImage) {
						setCoverImageUrl(firstImage.imageUrl || firstImage.downloadUrl || '');
					}
					setCardFormat(formatOf(cardData));
					setNewPages(somePages);
					setOpen(false);
				}
			} catch (err) {
				console.log({ 'Error getting card data: ': err });
			}
		}
	}, [id, mergeTags, previewCardOptions.previewProspect, previewCardOptions.prospectData, isPreviewModal, recipientId]);

	const handleTemplatePreview = async () => {
		let somePages: { (): JSX.Element }[] = [];
		try {
			const { card: cardData } = selectedTemplate;
			if (cardData.title) setCardTitle(cardData.title);
			if (cardData.stage) {
				const compileTemplate = _.template(JSON.stringify(cardData.cardPages));
				let compiledCardPages = cardData.cardPages;
				// if previewing card for prospects using merge tags then replace merge tags in cardpages
				if (isPreviewModal && previewCardOptions.previewProspect) {
					let dataWithFallback = previewCardOptions.prospectData;
					Object.keys(previewCardOptions.prospectData).forEach((key: string) => {
						if (!previewCardOptions.prospectData[key]) {
							const fallback = mergeTags.find((item) => item.tagName === key)?.fallback || '';
							dataWithFallback = {
								...dataWithFallback,
								[key]: fallback,
							};
						}
					});
					try {
						compiledCardPages = JSON.parse(compileTemplate(dataWithFallback));
					} catch (error) {
						console.log({ error });
					}
				} else if (recipientId) {
					const recipientData = await recipients_service.getRecipientById(recipientId);
					let recipientTagsData: any = {};
					if (recipientData) {
						recipientData.mergeTags.forEach((item: TMergeTag) => {
							recipientTagsData = {
								...recipientTagsData,
								[item.tagName]: item.value || item.fallback || '',
							};
						});
					}
					try {
						compiledCardPages = JSON.parse(compileTemplate(recipientTagsData));
					} catch (error) {
						console.log({ error });
					}
				} else if (!recipientId && (() => {
					const p: any = {};
					searchParams.forEach((v, k) => { if (k !== 'recipientId') p[k] = v; });
					return Object.keys(p).length > 0;
				})()) {
					const urlParamData: any = {};
					searchParams.forEach((value, key) => {
						if (key !== 'recipientId') urlParamData[key] = value;
					});
					try {
						const mergeTagRes = await card_services.getCardByIdForMergeTag(id);
						if (mergeTagRes?.mergeTags?.length) {
							mergeTagRes.mergeTags.forEach((tag: TMergeTag) => {
								if (urlParamData[tag.tagName] === undefined || urlParamData[tag.tagName] === '') {
									urlParamData[tag.tagName] = tag.fallback || '';
								}
							});
						}
					} catch (err) {
						console.log('Error fetching merge tags for URL params:', err);
					}
					try {
						compiledCardPages = JSON.parse(compileTemplate(urlParamData));
					} catch (error) {
						console.log({ error });
					}
				} else {
					let noProspectPreview: any = {};
					mergeTags.forEach((item) => {
						noProspectPreview[item.tagName] = item.fallback;
					});
					try {
						compiledCardPages = JSON.parse(compileTemplate(noProspectPreview));
					} catch (error) {
						console.log({ error });
					}
				}
				const updatedCardPages = await Promise.all(
					compiledCardPages.map(async (pageData: any) => {
						return pageData?.images?.length
							? await Promise.all(
								pageData?.images?.map(async (image: ImageObj, imageIndex: number) => {
									if (image.imageUrl || image.downloadUrl) {
										if (image.imageUrl) pageData.images[imageIndex].imageUrl = image.imageUrl;
										if (image.downloadUrl) pageData.images[imageIndex].downloadUrl = image.downloadUrl;
										return pageData;
									}
								})
							)
							: [pageData];
						// NOTE - return [pageData] because out expected output is an array of object(object being updated page data with blobs)
					})
				);
				const checkedCardPages = assembleForFormat(updatedCardPages, undefined, cardData.format);
				checkedCardPages.forEach((pageData: any, pageId: any) => {
					// NOTE - because of array.map each page is an array containing cardPage object at pageData[0]

					somePages.push(() => <CardPage stage={cardData.stage} pageData={pageData[0]} pageId={pageId} />);
				});
				setCardFormat(formatOf(cardData));
				setNewPages(somePages);
				somePages = [];
				setOpen(false);
			}
		} catch (err) {
			console.log({ 'Error getting card data: ': err });
		}
	};
	useEffect(() => {
		WebFont.load({
			google: {
				families: [
					'Comfortaa',
					'Josefin Sans',
					'Euphoria Script',
					'Oswald',
					'Alfa Slab One',
					'UnifrakturMaguntia',
					'Bangers',
					'Caveat',
					'DM Sans',
					'East Sea Dokdo',
					'Homemade Apple',
					'Kristi',
					'Lobster Two',
					'Maven Pro',
					'Monoton',
					'Montserrat',
					'Playfair Display',
					'Press Start 2P',
					'Raleway',
					'Roboto Slab',
					'Shadows Into Light',
					'Swanky and Moo Moo',
					'Bebas Neue',
					'Pacifico',
					'Secular One',
					'Teko',
					'Crimson Text',
					'Righteous',
					'Satisfy',
					'Kavoon',
					'Fredoka One',
					'IBM Plex Mono',
					'Questrial',
					'Barlow Semi Condensed',
					'Exo',
					'Play',
					'Lilita One',
					'Acme',
					'Chakra Petch',
					'Permanent Marker',
					'Orbitron',
					'Ubuntu Condensed',
					'Cinzel',
					'Spectral',
					'Covered By Your Grace',
					'Kalam',
					'Changa',
					'Space Mono',
					'Courgette',
					'Patua One',
					'Great Vibes',
					'Kaushan Script',
					'Cormorant',
					'Yeseva One',
					'Sora',
					'Oleo Script',
					'Outfit',
					'Yellowtail',
					'Montserrat Alternates',
					'Cookie',
					'Michroma',
					'Philosopher',
					'Lexend',
					'Urbanist',
					'Sawarabi Mincho',
					'Patrick Hand',
					'Concert One',
					'Gloria Hallelujah',
					'Sacramento',
					'Press Start 2P',
					'Poppins',
					'Inter',
					'Nunito',
					'Rubik',
					'Kanit',
					'Mulish',
					'Quicksand',
					'Inconsolata',
					'Titillium Web',
					'Dosis',
					'Libre Baskerville',
					'Prompt',
					'Irish Grover',
					'Sniglet',
					'Merienda One',
					'Rubik Moonrocks',
					'Seaweed Script',
					'Gabriela',
					'Averia Libre',
					'Viaoda Libre',
					'Grandstander',
					'Caladea',
					'Tillana',
					'Podkova',
					'Spinnaker',
					'Paprika',
					'Hanuman',
					'Kelly Slab',
					'Mirza',
					'Copse',
					'Marvel',
					'Calligraffitti',
					'Vollkorn SC',
					'Lemon',
					'Gugi',
					'Rakkas',
					'Bungee Shade',
					'Limelight',
					'Grenze Gotisch',
					'Cormorant Upright',
					'McLaren',
					'Turret Road',
					'Fondamento',
					'Oleo Script Swash Caps',
					'Sansita Swashed',
					'Rouge Script',
					'Fanwood Text',
					'Montez',
					'Mansalva',
					'Vast Shadow',
					'Mountains of Christmas',
					'Ruslan Display',
					'Charmonman',
					'Walter Turncoat',
					'Life Savers',
					'Yeon Sung',
					'Sail',
					'Gemunu Libre',
					'Vibur',
					'Cherry Cream Soda',
					'Sulphur Point',
					'Redressed',
					'Engagement',
					'Lovers Quarrel',
					'Monofett',
					'Chicle',
					'Sarina',
					'Croissant One',
					'Text Me One',
					'Wellfleet',
					'Margarine',
					'Mogra',
					'Mogra',
					'Zen Tokyo Zoo',
					'Praise',
					'Moulpali',
					'Trochut',
					'Taprom',
					'Sunshiney',
					'Vibes',
					'Nova Script',
					'Gwendolyn',
					'Kenia',
					'Tourney',
					'Warnes',
					'Ole',
					'Nerko One',
					'Goldman',
					'Germania One',
					'Supermercado One',
				],
			},
		});

		if (!!selectedTemplate) {
			handleTemplatePreview()
				.then(() => {
					setOpen(false);
				})
				.catch((err) => {
					console.log({ 'Error getting card data': err });
					setOpen(false);
				});
		} else {
			getCardData()
				.then(() => {
					setOpen(false);
				})
				.catch((err) => {
					console.log({ 'Error getting card data': err });
					setOpen(false);
				});
		}
	}, [getCardData, previewCardOptions.modalOpen]);

	// single and double cards have no spread to open: they are shown centred and already "opened"
	const isFlatFormat = cardFormat !== 'folded';

	const springProp = useSpring({
		scale: 1,
		from: { scale: 3 },
		config: { mass: 5, tension: 200, friction: 40, precision: 0.001 },
		delay: 200,
		immediate: isFlatFormat && prefersReducedMotion(),
	});
	const [isWhiteLabel, setisWhiteLabel] = useState(false);
	const [, setImage] = useState('');
	useEffect(() => {
		custom_domain_services
			.getCustomDomain()
			.then((whitelabelResponse) => {
				if (whitelabelResponse.status) {
					const { connectionStatus, sslStatus, logo } = whitelabelResponse.data;
					if (connectionStatus && sslStatus) {
						setisWhiteLabel(true);
						setImage(logo?.['Dark Logo']);
						return;
					}
				}
				// If no account-level white label found, check workspace custom domains
				const hostname = window.location.hostname;
				workspace_custom_domain_services
					.lookupWorkspaceDomain(hostname)
					.then((workspaceDomainRes: any) => {
						if (workspaceDomainRes?.data?.status === 'active') {
							setisWhiteLabel(true);
						}
					})
					.catch(() => {});
			})
			.catch((e) => console.log('Error fetching the custom domain', e));
	}, []);
	useEffect(() => {
		if (window.location.pathname.includes('viewcard')) {
			(async () => {
				try {
					await analyticsService.updateCardOpened(id, recipientId);
				} catch (error) {
					console.log({ error });
				}
			})();
		}
	}, []);
	useEffect(() => {
		if (!previewCardOptions.modalOpen) {
			setNewPages([]);
			setopenCard(true);
		}
	}, [previewCardOptions]);

	const confettiConfig: any = {
		angle: '111',
		spread: '155',
		startVelocity: '50',
		elementCount: '199',
		dragFriction: '0.25',
		duration: '6290',
		stagger: '14',
		width: '12px',
		height: '13px',
		perspective: '296px',
		colors: ['#f00', '#0f0', '#00f'],
	};

	const getBrightness = (color: string) => {
		const hex = color?.replace('#', '');
		const r = parseInt(hex?.substring(0, 2), 16);
		const g = parseInt(hex?.substring(2, 4), 16);
		const b = parseInt(hex?.substring(4, 6), 16);
		const brightness = (r * 299 + g * 587 + b * 114) / 1000;
		return brightness;
	};

	// Determine the appropriate text color based on brightness
	const getTextColor = (backgroundColor: string) => {
		const brightness = getBrightness(backgroundColor);
		return brightness > 128 ? '#3730a3' : '#e0e7ff';
	};

	const isDesktop = width >= desktopBreakpoint;

	// Sensible default backdrop so cards (which are often white) stay visible even
	// when the workspace has no custom preview colors configured.
	const DEFAULT_BG = 'radial-gradient(circle at 50% 30%, #eef2ff 0%, #dbe2ff 45%, #b7c3f5 100%)';
	const ps = currentWorkspace?.previewSetting;
	const bgStyle = {
		background: ps?.solid
			? ps.solidColor || DEFAULT_BG
			: ps?.startColor && ps?.endColor
				? `radial-gradient(at center bottom, ${ps.startColor}, ${ps.endColor})`
				: DEFAULT_BG,
	};

	return (
		<ViewCardContext.Provider value={{ isCardOpened: (!openCard || isFlatFormat) && isDesktop }}>
		  {/* Scrollable wrapper — allows mobile to scroll past card to reach engagement section */}
		  <div ref={scrollWrapperRef} className={routePath.includes('viewcard') && !isDesktop ? 'w-screen h-screen overflow-y-auto' : 'contents'} style={!isDesktop && routePath.includes('viewcard') ? bgStyle : undefined}>
			<div
				className="flex items-center relative justify-center w-screen h-screen flex-shrink-0"
				style={bgStyle}
			>
				<div className="absolute inset-2/4">
					{isConfettiOn && width > breakpoint && <Confetti active={isConfettiOn} config={confettiConfig} />}
				</div>
				{/* //? Outer Layout - Background */}
				<div className={!!isPreviewModal ? 'hidden' : ' flex flex-wrap no-scrollbar'}>
					<div className=" w-screen md:flex hidden" style={{ height: '100vh' }}></div>
				</div>
				{!fullCardData?.isVideoCard && (
				<div
					style={{ fontFamily: 'Urbanist', display: routePath.includes('viewcard') ? '' : 'none' }}
					className="absolute  h-20 md:top-8 top-20 text-gray-800 font-semibold md:text-xl text-base  flex m-5  justify-center"
				>
					{cardHeader ? cardHeader : t('viewcard.header', { name: recipientName, sender: senderName })}
				</div>
				)}
				{/* Top-left stack: logo above the language switcher. Kept in one container
				    so they can never overlap (previously both were pinned top-left).
				    z-[70] keeps the switcher clickable above the envelope intro overlay (z-60). */}
				{routePath.includes('viewcard') && (
					<div className="absolute top-4 left-4 z-[70] flex flex-col items-start gap-2">
						{!fullCardData?.isVideoCard && currentWorkspace?.previewSetting?.cardClanLogo && (
							// Height-constrained with w-auto — the image carries no intrinsic size cap,
							// so without this it renders at natural size and overflows (the "too big" bug).
							<img
								className="object-contain object-left h-8 md:h-10 w-auto max-w-[140px] drop-shadow-sm"
								alt="logo"
								src={currentWorkspace?.previewSetting?.companyLogo}
							/>
						)}
						<LanguageSwitcher variant="compact" />
					</div>
				)}
				{/* "Create your own" CTA — recipient view, non-white-label only (drives new signups) */}
				{!fullCardData?.isVideoCard && routePath.includes('viewcard') && !isWhiteLabel && !isPreview && (
					<a
						href="https://www.cardclan.io"
						target="_blank"
						rel="noreferrer"
						className="absolute top-4 right-4 z-40 inline-flex items-center gap-1.5 px-4 py-2 bg-indigo hover:bg-indigo/90 text-white text-xs md:text-sm font-semibold rounded-full shadow-lg transition"
					>
						<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
							<path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
						</svg>
						{t('viewcard.createYourOwn')}
					</a>
				)}

				{/* Video card view — respects deliveryMode setting */}
				{fullCardData?.isVideoCard && routePath.includes('viewcard') && (() => {
					const deliveryMode = fullCardData?.videoCardSettings?.deliveryMode || 'board';
					const showBoard = deliveryMode === 'board' || !renderedVideoUrl;
					const showVideo = deliveryMode === 'video' && !!renderedVideoUrl;

					return (
						<>
							{/* Logo for video cards — workspace logo or CardClan fallback.
							    Sits below the top-left language switcher (top-16) so the two don't overlap. */}
							{routePath.includes('viewcard') && (
								<div className="absolute top-16 left-4 z-50">
									<img
										className="h-8 md:h-10 object-contain object-left w-auto max-w-[140px]"
										alt="logo"
										src={currentWorkspace?.previewSetting?.companyLogo || 'https://res.cloudinary.com/interstellus/image/upload/v1675421128/Cardclan-Logo-final_t6l4ie.png'}
									/>
								</div>
							)}

							{/* "Create your own" CTA — only for non-white-label */}
							{routePath.includes('viewcard') && !isWhiteLabel && !isPreview && (
								<a
									href="https://www.cardclan.io"
									target="_blank"
									rel="noreferrer"
									className="absolute top-4 right-4 z-50 inline-flex items-center gap-1.5 px-4 py-2 bg-indigo hover:bg-indigo/90 text-white text-sm font-semibold rounded-full shadow-lg transition"
								>
									<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
										<path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
									</svg>
									Create your own
								</a>
							)}

							{/* Board view */}
							{showBoard && (
								<div className="absolute inset-0 z-30 overflow-y-auto" style={{
									backgroundColor: fullCardData?.boardSettings?.backgroundColor || 'rgba(255,255,255,0.95)',
								}}>
									<TileBoardViewer
										cardId={id}
										boardSettings={fullCardData?.boardSettings}
										cardTitle={cardTitle}
									/>
									{/* Engagement below board — hidden in preview mode */}
									{routePath.includes('viewcard') && !isPreview && (
										<div className="w-full max-w-2xl mx-auto px-4 pb-12">
											<ReactionBar cardId={id} recipientEmail={recipientId || recipientEmailFromParams || 'anonymous@viewcard.local'} />
											<CommentSection cardId={id} recipientEmail={recipientId || recipientEmailFromParams || 'anonymous@viewcard.local'} />
										</div>
									)}
								</div>
							)}

							{/* Rendered video view */}
							{showVideo && (
								<div className="absolute inset-0 z-30 overflow-y-auto" style={{
									background: fullCardData?.boardSettings?.backgroundUrl
										? `url(${fullCardData.boardSettings.backgroundUrl}) center/cover no-repeat`
										: fullCardData?.boardSettings?.backgroundColor || '#1a1a2e',
								}}>
									{/* Spacer for logo */}
									<div className="h-16" />
									{/* Title header */}
									<div className="px-6 py-5 text-white text-center" style={{ backgroundColor: fullCardData?.boardSettings?.headerColor || '#4F46E5' }}>
										<h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: fullCardData?.boardSettings?.titleFont || 'Inter' }}>
											{cardTitle}
										</h1>
									</div>

									{/* Video player */}
									<div className="flex flex-col items-center px-4 py-8">
										<div className="w-full max-w-3xl">
											<div className="rounded-2xl overflow-hidden shadow-2xl bg-black">
												<video
													src={renderedVideoUrl}
													controls
													autoPlay
													className="w-full"
												/>
											</div>
											{/* Download button */}
											<div className="flex justify-center mt-4">
												<button
													onClick={async () => {
														try {
															const resp = await fetch(renderedVideoUrl);
															const blob = await resp.blob();
															const url = window.URL.createObjectURL(blob);
															const a = document.createElement('a');
															a.href = url;
															a.download = `${cardTitle || 'video'}.mp4`;
															document.body.appendChild(a);
															a.click();
															document.body.removeChild(a);
															window.URL.revokeObjectURL(url);
														} catch {
															window.open(renderedVideoUrl, '_blank');
														}
													}}
													className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/90 hover:bg-white text-gray-800 rounded-xl text-sm font-medium shadow-lg transition cursor-pointer"
												>
													<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
														<path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
													</svg>
													Download Video
												</button>
											</div>

											{/* Engagement below video — hidden in preview mode */}
											{routePath.includes('viewcard') && !isPreview && (
												<div className="mt-8">
													<ReactionBar cardId={id} recipientEmail={recipientId || recipientEmailFromParams || 'anonymous@viewcard.local'} />
													<CommentSection cardId={id} recipientEmail={recipientId || recipientEmailFromParams || 'anonymous@viewcard.local'} />
												</div>
											)}
										</div>
									</div>
								</div>
							)}

							{/* Effects overlay — rendered outside scrollable containers */}
							<BoardEffects
								effect={fullCardData?.boardSettings?.effect || 'none'}
								active={(fullCardData?.boardSettings?.effect || 'none') !== 'none'}
							/>
						</>
					);
				})()}

				{/* Envelope intro — only for real recipient views (not the sender's preview). */}
				{!!newPages.length && !renderedVideoUrl && !fullCardData?.isVideoCard &&
					showEnvelope && routePath.includes('viewcard') && !isPreviewModal && (
						<EnvelopeIntro
							onOpened={() => setShowEnvelope(false)}
							recipientName={recipientName}
							senderName={senderName}
							coverImageUrl={coverImageUrl}
						/>
					)}

				{!!newPages.length && !renderedVideoUrl && !fullCardData?.isVideoCard &&
					!(showEnvelope && routePath.includes('viewcard') && !isPreviewModal) &&
					(isDesktop ? (
						<a.div
							style={springProp}
							className={`p-10 absolute 2xl:top-10 -top-4  ${!!isPreviewModal
								? ''
								: isFlatFormat
									? '2xl:pt-20 md:pt-36 z-30'
									: openCard
										? '2xl:pt-20 md:pt-36 -ml-64 z-30'
										: '2xl:pt-20 md:pt-36 ml-10 z-30'
								} `}
							onClick={() => setopenCard(false)}
							id="card-capture-area"
						>
							{cardFormat === 'single' ? (
								<SingleCardViewer pages={newPages} />
							) : cardFormat === 'double' ? (
								<PostcardViewer pages={newPages} />
							) : (
								<CardViewer
									pages={newPages}
									mainClass="grid grid-cols-2 cursor-pointer  "
									leftPageClass="absolute right-20  "
									rightPageClass=" absolute left-0 "
								/>
							)}
						</a.div>
					) : (
						<div className="absolute" id="card-capture-area">
							<CardViewerMobile pages={newPages} hideHint={cardFormat === 'single'} />
						</div>
					))}
				<div
					style={{ display: routePath.includes('viewcard') ? '' : 'none' }}
					className={isCTA ? 'absolute bottom-24' : 'hidden'}
				>
					<a
						href={`https://${ctaLink}`}
						target="_blank"
						className="px-3 py-2 text-white bg-indigo rounded-lg"
						rel="noreferrer"
					>
						{ctaText}
					</a>
				</div>
				<div
					style={{
						bottom: '70px',
						left: '45%',
						color: ps?.solid
							? getTextColor(ps.solidColor || '#dbe2ff')
							: getTextColor(ps?.startColor || '#dbe2ff'),
					}}
					// #4F46E5
					className={(openCard || cardFormat === 'double') && !fullCardData?.isVideoCard && cardFormat !== 'single' ? 'absolute  z-40  xl:block  hidden ' : 'hidden'}
				>
					<ToolTipCard text={cardFormat === 'double' ? t('viewcard.tapToFlip') : t('viewcard.clickToOpen')} textFontSize="18px" isPreviewModal={isPreviewModal} />
				</div>

				{/* Engagement panel — desktop: fixed right sidebar */}
				{routePath.includes('viewcard') && id && !fullCardData?.isVideoCard && (
					<div
						className="hidden xl:flex"
						style={{
							position: 'fixed',
							right: '16px',
							top: '50%',
							transform: 'translateY(-50%)',
							flexDirection: 'column',
							alignItems: 'stretch',
							gap: '0',
							width: '300px',
							zIndex: 50,
							overflowY: 'auto',
							maxHeight: '85vh',
						}}
					>
						{fullCardData?.event?.enabled && (
							<EventPanel cardId={id} event={fullCardData.event} recipientId={recipientId || undefined} token={rsvpToken || undefined} readOnly={!!isPreviewModal} />
						)}
						<div className="flex gap-2 mb-2">
							<DownloadButton cardId={id} recipientEmail={recipientId || recipientEmailFromParams || 'anonymous@viewcard.local'} coverImageUrl={coverImageUrl} cardTitle={cardTitle} />
							<ShareButton cardId={id} cardTitle={cardTitle} />
						</div>
						<ReactionBar cardId={id} recipientEmail={recipientId || recipientEmailFromParams || 'anonymous@viewcard.local'} readOnly={!!isPreviewModal} />
						<CommentSection cardId={id} recipientEmail={recipientId || recipientEmailFromParams || 'anonymous@viewcard.local'} readOnly={!!isPreviewModal} />
					</div>
				)}

				{/* Event panel for sender preview (RSVP disabled). The modal box is transformed, so `fixed` is relative to it:
				    desktop — right sidebar as on /viewcard; smaller screens — a scrollable sheet above the download button. */}
				{!!isPreviewModal && !!id && !fullCardData?.isVideoCard && fullCardData?.event?.enabled && (
					<>
						<div
							className="hidden xl:block"
							style={{ position: 'fixed', right: '16px', top: '50%', transform: 'translateY(-50%)', width: '300px', zIndex: 50, overflowY: 'auto', maxHeight: '85%' }}
						>
							<EventPanel cardId={id} event={fullCardData.event} readOnly />
						</div>
						<div
							className="xl:hidden"
							style={{ position: 'fixed', left: '12px', right: '12px', bottom: '80px', zIndex: 50, overflowY: 'auto', maxHeight: '40%' }}
						>
							<EventPanel cardId={id} event={fullCardData.event} readOnly />
						</div>
					</>
				)}

				{/* Download button for sender preview (card editor + dashboard preview modal) — non-video cards only */}
				{!!isPreviewModal && !!id && !fullCardData?.isVideoCard && (
					<div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
						<DownloadButton cardId={id} recipientEmail="preview@sender.local" skipAnalytics={true} coverImageUrl={coverImageUrl} cardTitle={cardTitle} />
					</div>
				)}

				{/* Scroll-down hint on mobile — tells user reactions/comments are below */}
				{routePath.includes('viewcard') && id && !isDesktop && !hasScrolled && !fullCardData?.isVideoCard && (
					<div className="absolute bottom-16 left-0 right-0 flex flex-col items-center z-40 animate-bounce">
						<span className="text-sm font-semibold text-white bg-black bg-opacity-40 backdrop-blur-sm rounded-full px-5 py-2 shadow-lg">
							{t('viewcard.scrollToReact')}
						</span>
					</div>
				)}
			</div>

			{/* Engagement panel — mobile/tablet: stacked below card + CTA */}
			{routePath.includes('viewcard') && id && !fullCardData?.isVideoCard && (
				<div className="xl:hidden w-full px-4 pt-2 pb-8 flex flex-col items-center">
					<div className="w-full max-w-md">
						{fullCardData?.event?.enabled && (
							<EventPanel cardId={id} event={fullCardData.event} recipientId={recipientId || undefined} token={rsvpToken || undefined} readOnly={!!isPreviewModal} />
						)}
						<div className="flex justify-center gap-2 mb-2">
							<DownloadButton cardId={id} recipientEmail={recipientId || recipientEmailFromParams || 'anonymous@viewcard.local'} coverImageUrl={coverImageUrl} cardTitle={cardTitle} />
							<ShareButton cardId={id} cardTitle={cardTitle} />
						</div>
						<ReactionBar cardId={id} recipientEmail={recipientId || recipientEmailFromParams || 'anonymous@viewcard.local'} readOnly={!!isPreviewModal} />
						<CommentSection cardId={id} recipientEmail={recipientId || recipientEmailFromParams || 'anonymous@viewcard.local'} readOnly={!!isPreviewModal} />
					</div>
				</div>
			)}
		  </div>{/* end scrollable wrapper */}

			<Modal open={open} className="-mt-40">
				<div className="bg-white p-10 text-radicalRed rounded-md md:mx-0 mx-3 ">
					<h1 className="text-center"> {I18n.t('generate_card_preview')}</h1>
					<div className="flex place-items-center justify-center pt-5 ">
						<div className="loader"></div>
					</div>
				</div>
			</Modal>
			{/* </AppContainer>0 */}
			{fullCardData?.music?.url && routePath.includes('viewcard') && !fullCardData?.isVideoCard && (
				<MusicPlayer musicUrl={fullCardData.music.url} musicName={fullCardData.music.name} />
			)}
		</ViewCardContext.Provider>
	);
};

export default React.memo(ViewCard);
